"use client";

import { useState } from "react";
import Image from "next/image";
import { motion, useReducedMotion } from "framer-motion";
import { Button, ButtonLink } from "@/components/ui/button";
import { Countdown } from "@/components/countdown";
import { event } from "@/lib/content";

const EASE = [0.2, 0, 0, 1] as const;

const heroMeta = [
  { value: "02 y 03", value2: "Septiembre", sub: "2026", big: true },
  { value: event.venue, value2: undefined, sub: event.venueAddress, big: false },
  { value: "2 días", value2: undefined, sub: "Presencial y gratuito.", big: true },
  { value: "Cupos limitados", value2: undefined, sub: "Exclusivo C-level, previa inscripción.", big: false },
] as const;

export function Hero() {
  const reduced = useReducedMotion();
  const [isStreamingOpen, setIsStreamingOpen] = useState(false);

  return (
    <header
      id="top"
      className="relative min-h-screen overflow-hidden flex flex-col bg-[#161618] text-white px-6 sm:px-10 pt-28 sm:pt-36 pb-12 sm:pb-16"
    >
      {/* Background image — Impacta texture */}
      <Image
        src="/img/fondo-textura-impacta.webp"
        alt=""
        fill
        priority
        sizes="100vw"
        className="object-cover object-top pointer-events-none select-none"
      />

      {/* Bottom gradient for type contrast over the stats row */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, transparent 55%, rgba(20,20,20,0.7) 100%)",
        }}
        aria-hidden
      />

      <div className="relative z-10 mx-auto w-full max-w-[1280px] flex-1 flex flex-col justify-between">
        {/* Top group — headline, sub-head, CTAs */}
        <div>
          {/* Headline */}
          <motion.h1
            initial={reduced ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="font-[family-name:var(--font-display)] font-bold leading-[1.12] tracking-[-0.035em] text-white max-w-[30ch] m-0"
            style={{ fontSize: "clamp(38px, 6vw, 80px)" }}
          >
            El punto de encuentro
            <br />
            de IA para quienes
            <br />
            <span className="shimmer-text">toman decisiones</span>
          </motion.h1>

          {/* Sub-head + CTAs */}
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.16 }}
            className="mt-5 sm:mt-7 max-w-[960px]"
          >
            <p className="font-[family-name:var(--font-body)] text-[clamp(15px,1.12vw,18px)] leading-[1.5] text-white/85 m-0">
              Un evento para líderes que están definiendo el futuro de sus organizaciones con inteligencia artificial. Dos días de inmersión en IA aplicada, casos reales y networking estratégico. Cupos limitados.
            </p>
            <div className="flex flex-wrap gap-3 mt-8 justify-start max-w-[600px]">
              <ButtonLink href="#form" variant="primary" size="lg">
                Quiero participar →
              </ButtonLink>
              <Button
                type="button"
                variant="inverse"
                size="lg"
                onClick={() => setIsStreamingOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={isStreamingOpen}
              >
                Ve el streaming en vivo ↗
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Cuenta regresiva al evento */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
          className="mt-14"
        >
          <Countdown />
        </motion.div>

        {/* Bottom meta row */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.24 }}
          className="mt-10 pt-7 border-t border-white/15 grid grid-cols-2 sm:grid-cols-4 gap-y-8 gap-x-6"
        >
          {heroMeta.map((m) => (
            <div key={m.value}>
              <div
                className="font-[family-name:var(--font-display)] font-bold text-white leading-[1.05] tracking-[-0.02em]"
                style={{ fontSize: m.big ? "clamp(28px, 2.8vw, 40px)" : "clamp(19px, 1.6vw, 22px)" }}
              >
                {m.value}
                {m.value2 && (
                  <>
                    <br />
                    {m.value2}
                  </>
                )}
              </div>
              {m.sub && (
                <div className="font-[family-name:var(--font-body)] text-[13px] text-white/55 leading-snug mt-2 max-w-[230px]">
                  {m.sub}
                </div>
              )}
            </div>
          ))}
        </motion.div>
      </div>

      {isStreamingOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-5 backdrop-blur-sm"
          onClick={() => setIsStreamingOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="streaming-dialog-title"
            className="relative w-full max-w-md rounded-3xl border border-white/15 bg-[#18181A] p-6 sm:p-7 shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsStreamingOpen(false)}
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/15 text-xl leading-none text-white/70 transition-colors hover:border-white/35 hover:text-white"
              aria-label="Cerrar selector de streaming"
            >
              ×
            </button>
            <p className="font-[family-name:var(--font-body)] text-[11px] font-bold uppercase tracking-[0.18em] text-[#1DD2B3]">
              Impacta IA en vivo
            </p>
            <h2
              id="streaming-dialog-title"
              className="mt-2 pr-8 font-[family-name:var(--font-display)] text-3xl font-bold leading-none tracking-[-0.025em] text-white"
            >
              Elige tu jornada
            </h2>
            <p className="mt-3 font-[family-name:var(--font-body)] text-sm leading-relaxed text-white/65">
              Selecciona el día que quieres ver en Streaming.
            </p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <ButtonLink
                href="https://www.youtube.com/watch?v=4N6I6PXycMw"
                variant="primary"
                size="md"
                className="w-full !bg-[#1DD2B3] text-ink hover:!bg-[#3BE2C7] hover:shadow-[0_12px_28px_rgba(29,210,179,0.28)]"
                target="_blank"
                rel="noreferrer"
              >
                Ver Día 1 ↗
              </ButtonLink>
              <ButtonLink
                href="https://www.youtube.com/live/NJu13g38nDc"
                variant="primary"
                size="md"
                className="w-full !bg-[#ED1E79] text-white hover:!bg-[#F14B94] hover:shadow-[0_12px_28px_rgba(237,30,121,0.28)]"
                target="_blank"
                rel="noreferrer"
              >
                Ver Día 2 ↗
              </ButtonLink>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
